/**
 * Listado de **rollos** de la sede activa (galvanizada + acero/aluminio).
 *
 * Presentacional: recibe el payload de `GET /inventario/rollos` y los callbacks de
 * acción (consumir/borrar) que la pantalla cablea a las mutaciones. Agrupa la
 * galvanizada por calibre y formato (4x8 / 1x2) y el acero por perfil, con un
 * total por grupo, replicando la organización del SSR. Las acciones de
 * consumir/borrar se muestran solo si `canManage` (permiso `manage_inventory`).
 */
import { Button } from "@/components/ui";

import { findCalibreGalv, findPerfilAcero } from "../constants";
import { formatKg, formatMetros, pctAcero, pctGalv } from "../format";
import type { AceroData, GalvData, RolloAcero, RolloGalv } from "../types";
import type { ConsumeTarget } from "./ConsumeRolloDialog";

interface RollosListProps {
  galv: GalvData;
  acero: AceroData;
  canManage: boolean;
  onConsume: (target: ConsumeTarget) => void;
  onDelete: (rolloId: number, label: string) => void;
}

export function RollosList({ galv, acero, canManage, onConsume, onDelete }: RollosListProps) {
  const calibres = Object.keys(galv).sort();
  const perfiles = Object.keys(acero).sort();

  const hayGalv = calibres.some(
    (cal) => galv[cal]["4x8"].length > 0 || galv[cal]["1x2"].length > 0,
  );
  const hayAcero = perfiles.some((p) => acero[p].length > 0);

  if (!hayGalv && !hayAcero) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">
        No hay rollos registrados en esta sede.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {hayGalv ? (
        <section aria-labelledby="rollos-galv-heading" className="flex flex-col gap-3">
          <h3 id="rollos-galv-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Galvanizada (kg)
          </h3>
          {calibres.map((cal) => {
            const formatos = galv[cal];
            const label = findCalibreGalv(cal)?.label ?? `Cal. ${cal}`;
            return (["4x8", "1x2"] as const).map((fmt) => {
              const lotes = formatos[fmt];
              if (lotes.length === 0) {
                return null;
              }
              return (
                <GalvGroup
                  key={`${cal}-${fmt}`}
                  label={`${label} · ${fmt}`}
                  lotes={lotes}
                  canManage={canManage}
                  onConsume={onConsume}
                  onDelete={onDelete}
                />
              );
            });
          })}
        </section>
      ) : null}

      {hayAcero ? (
        <section aria-labelledby="rollos-acero-heading" className="flex flex-col gap-3">
          <h3 id="rollos-acero-heading" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Acero / Aluminio (m)
          </h3>
          {perfiles.map((perfilId) => {
            const lotes = acero[perfilId];
            if (lotes.length === 0) {
              return null;
            }
            const label = findPerfilAcero(perfilId)?.label ?? perfilId;
            return (
              <AceroGroup
                key={perfilId}
                label={label}
                lotes={lotes}
                canManage={canManage}
                onConsume={onConsume}
                onDelete={onDelete}
              />
            );
          })}
        </section>
      ) : null}
    </div>
  );
}

interface GalvGroupProps {
  label: string;
  lotes: RolloGalv[];
  canManage: boolean;
  onConsume: (target: ConsumeTarget) => void;
  onDelete: (rolloId: number, label: string) => void;
}

function GalvGroup({ label, lotes, canManage, onConsume, onDelete }: GalvGroupProps) {
  const totalKg = lotes.reduce((sum, lote) => sum + (lote.kg || 0), 0);
  return (
    <div className="rounded-md border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-sm text-slate-500">
          {lotes.length} lote(s) · {formatKg(totalKg)}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {lotes.map((lote) => (
          <li key={lote.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <span className="min-w-[80px] text-sm font-semibold text-slate-800">
              {lote.kg.toFixed(1)} kg
            </span>
            <span className="text-xs text-slate-500">{pctGalv(lote)}%</span>
            {lote.nota ? (
              <span className="flex-1 truncate text-xs italic text-slate-400">{lote.nota}</span>
            ) : (
              <span className="flex-1" />
            )}
            <span className="text-xs text-slate-400">{lote.fecha}</span>
            {canManage ? (
              <span className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    onConsume({ id: lote.id, label: `${label} · ${lote.kg.toFixed(1)} kg`, unidad: "kg", disponible: lote.kg })
                  }
                >
                  Consumir
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onDelete(lote.id, `${label} · ${lote.kg.toFixed(1)} kg`)}
                  aria-label={`Eliminar lote ${label}`}
                >
                  Eliminar
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface AceroGroupProps {
  label: string;
  lotes: RolloAcero[];
  canManage: boolean;
  onConsume: (target: ConsumeTarget) => void;
  onDelete: (rolloId: number, label: string) => void;
}

function AceroGroup({ label, lotes, canManage, onConsume, onDelete }: AceroGroupProps) {
  const totalMetros = lotes.reduce((sum, lote) => sum + (lote.metros || 0), 0);
  return (
    <div className="rounded-md border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="text-sm text-slate-500">
          {lotes.length} lote(s) · {formatMetros(totalMetros)}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {lotes.map((lote) => (
          <li key={lote.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
            <span className="min-w-[80px] text-sm font-semibold text-slate-800">
              {lote.metros.toFixed(1)} m
            </span>
            <span className="text-xs text-slate-500">{pctAcero(lote)}%</span>
            {lote.nota ? (
              <span className="flex-1 truncate text-xs italic text-slate-400">{lote.nota}</span>
            ) : (
              <span className="flex-1" />
            )}
            <span className="text-xs text-slate-400">{lote.fecha}</span>
            {canManage ? (
              <span className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    onConsume({ id: lote.id, label: `${label} · ${lote.metros.toFixed(1)} m`, unidad: "m", disponible: lote.metros })
                  }
                >
                  Consumir
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => onDelete(lote.id, `${label} · ${lote.metros.toFixed(1)} m`)}
                  aria-label={`Eliminar lote ${label}`}
                >
                  Eliminar
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
